import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash } from "@phosphor-icons/react";

const INSTRUCTIONS_PLACEHOLDER =
  "Example:\n• Write professionally\n• Add FAQs\n• Improve SEO\n• Beginner friendly\n• Keep under 1500 words";

/**
 * Optional reference URLs + AI instructions fields shared by Generate
 * dialogs and the Article detail AI Update section.
 */
export function ArticleAiExtrasFields({
  referenceUrls,
  setReferenceUrls,
  instructions,
  setInstructions,
  instructionsRequired = false,
  idPrefix = "aa-ai",
}) {
  const updateUrl = (idx, value) => {
    setReferenceUrls((prev) => prev.map((u, i) => (i === idx ? value : u)));
  };
  const addUrl = () => setReferenceUrls((prev) => [...prev, ""]);
  const removeUrl = (idx) => {
    setReferenceUrls((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [""];
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
          Research / policy URLs{" "}
          <span className="font-normal text-slate-400">(recommended)</span>
        </label>
        <p className="text-[11px] text-slate-500 mb-2">
          Paste official shipping, returns, membership/rewards, price-match,
          or store-pickup help pages. These unlock a full shopping guide.
          Without them we write an informed checklist guide — never invent
          policies.
        </p>
        <div className="space-y-2">
          {referenceUrls.map((url, idx) => (
            <div key={idx} className="flex gap-2">
              <Input
                type="url"
                placeholder="https://example.com/article"
                value={url}
                onChange={(e) => updateUrl(idx, e.target.value)}
                className="h-9 text-sm"
                data-testid={`${idPrefix}-ref-url-${idx}`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 h-9 px-2"
                onClick={() => removeUrl(idx)}
                disabled={referenceUrls.length <= 1 && !url}
                data-testid={`${idPrefix}-ref-remove-${idx}`}
              >
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1.5 h-8 text-xs"
          onClick={addUrl}
          data-testid={`${idPrefix}-ref-add`}
        >
          <Plus size={12} /> Add URL
        </Button>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
          AI instructions{" "}
          {instructionsRequired ? (
            <span className="text-rose-600">*</span>
          ) : (
            <span className="font-normal text-slate-400">(optional)</span>
          )}
        </label>
        <Textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={INSTRUCTIONS_PLACEHOLDER}
          rows={5}
          className="text-sm resize-y min-h-[100px]"
          data-testid={`${idPrefix}-instructions`}
        />
      </div>
    </div>
  );
}

/** Build API payload fields; drops empty URLs / blank instructions. */
export function buildAiExtrasPayload(referenceUrls, instructions) {
  const urls = (referenceUrls || [])
    .map((u) => (u || "").trim())
    .filter(Boolean);
  const instr = (instructions || "").trim();
  const payload = {};
  if (urls.length) payload.reference_urls = urls;
  if (instr) payload.instructions = instr;
  return payload;
}
